import React from 'react';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import type { McpConnection } from '../../generated/api/types.gen.js';
import { Badge } from '../../components/ui/index.js';

interface McpTrustCellProps {
  connection: McpConnection;
}

const humanize = (value: string) => value.replaceAll('_', ' ').replace(/^\w/, (c) => c.toUpperCase());

function findingText(finding: Record<string, unknown>): string {
  return `${String(finding.toolName ?? 'Tool')}: ${String(finding.reason ?? finding.type ?? 'review required')}`;
}

/** Trust state of an MCP server: the review status, when its tool list last changed and any poisoning findings. */
export const McpTrustCell: React.FC<McpTrustCellProps> = ({ connection }) => {
  const findings = connection.toolPoisoningFindings ?? [];
  const danger = findings.length > 0 || connection.trustStatus === 'needs_review';
  const variant = danger ? 'danger' : connection.trustStatus === 'changed' ? 'warning' : 'success';

  return (
    <div className="space-y-1">
      <Badge variant={variant} icon={danger ? <ShieldAlert size={13} /> : <ShieldCheck size={13} />}>
        {humanize(connection.trustStatus || 'unreviewed')}
      </Badge>
      {connection.toolListChangedAt && (
        <p className="text-xs text-muted">Tools changed {new Date(connection.toolListChangedAt).toLocaleString()}</p>
      )}
      {findings.slice(0, 2).map((finding, index) => (
        <p key={`${connection.id}-finding-${index}`} className="text-xs text-danger">
          {findingText(finding)}
        </p>
      ))}
      {findings.length > 2 && <p className="text-xs text-muted">+{findings.length - 2} more checks</p>}
    </div>
  );
};
